import { Factory } from 'fishery'
import { v4 as uuidV4 } from 'uuid'
import {
  type OpalApiComponent,
  type OpalApiResponseDocument,
} from '@opallabs/api'
import { workflowResourceFactory } from '../resource_objects/workflow'
import { workflowStageResourceFactory } from '../resource_objects/workflow_stage'

type Workflows = OpalApiResponseDocument<'ReadWorkflowsV3', 200>
type Stage = OpalApiComponent<'V3', 'workflow_stage'>

type WorkflowsTransientParams = {
  /**
   * The stages after the leading pause, which the factory always adds.
   *
   * @default one inactive task stage
   */
  stages: Array<Stage>
  /**
   * The stage the workflow is waiting on, after the leading pause. Leave it out
   * for a workflow that has not started: it waits on the leading pause, the
   * state the server leaves it in once it has a context. Set that stage's own
   * `status` to `active` too.
   */
  activeStage?: Stage
  /** Declined approvals on the active stage. @default 0 */
  declined: number
}

const progressFor = (
  declined: number,
  activeStage?: Stage
): Workflows['data'][number]['attributes']['progress'] => {
  switch (activeStage?.attributes.stage_type) {
    case 'task':
      return 'tasks_pending'
    case 'approval':
      return declined > 0 ? 'declined' : 'approval_pending'
    case 'circuit_breaker':
      return 'paused'
    default:
      return 'not_started'
  }
}

/**
 * `ReadWorkflowsV3` for one block's workflow: a leading pause (the workflow's
 * start gate, which plasma hides) followed by `stages`.
 */
export const workflowsFactory = Factory.define<
  Workflows,
  WorkflowsTransientParams
>(({ transientParams }) => {
  const {
    stages = [
      workflowStageResourceFactory.build({
        attributes: { stage_type: 'task', position: 1 },
      }),
    ],
    activeStage,
    declined = 0,
  } = transientParams
  const pause = workflowStageResourceFactory.build({
    attributes: {
      stage_type: 'circuit_breaker',
      position: 0,
      status: activeStage ? 'complete' : 'active',
    },
    relationships: {
      assignments: { data: [{ id: uuidV4(), type: 'workflow_assignment' }] },
    },
  })
  const allStages = [pause, ...stages]
  const latestStage = activeStage ?? pause
  const countOf = (stageType: Stage['attributes']['stage_type']) =>
    allStages.filter(({ attributes }) => attributes.stage_type === stageType)
      .length

  const workflow = workflowResourceFactory.build({
    attributes: { progress: progressFor(declined, activeStage) },
    relationships: {
      current_stage: {
        data: activeStage
          ? { id: activeStage.id, type: 'workflow_stage' }
          : null,
      },
      stages: {
        data: allStages.map(({ id }) => ({ id, type: 'workflow_stage' })),
      },
    },
    meta: {
      latest_active_stage: {
        stage_type: latestStage.attributes.stage_type,
        resource_identifier: {
          data: { id: latestStage.id, type: 'workflow_stage' },
        },
        assignment_counts: {
          all: { complete: 0, incomplete: 1 },
          current_user: { complete: 0, incomplete: 0 },
          declined,
        },
      },
      completed_stages: allStages.filter(
        ({ attributes }) => attributes.status === 'complete'
      ).length,
      stage_type_count: {
        approval: countOf('approval'),
        task: countOf('task'),
        circuit_breaker: countOf('circuit_breaker'),
      },
    },
  })

  return {
    data: [workflow],
    included: allStages,
    meta: { resource_type: 'workflow', total: 1 },
    links: {
      self: '/v3/workflows',
      first: '/v3/workflows',
      prev: null,
      next: null,
      last: '/v3/workflows',
    },
  } satisfies Workflows
})
